import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Shield, User, Mail, Lock, Save, Settings, Image, Upload, X } from 'lucide-react';

interface UpdateProfileForm {
  name: string;
  email: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

export const AdminSettings: React.FC = () => {
  const { agent } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [settingsSuccessMessage, setSettingsSuccessMessage] = useState('');
  const [settingsErrorMessage, setSettingsErrorMessage] = useState('');
  const [logoSuccessMessage, setLogoSuccessMessage] = useState('');
  const [logoErrorMessage, setLogoErrorMessage] = useState('');
  const [systemName, setSystemName] = useState('Itororo SAC');
  const [systemLogo, setSystemLogo] = useState<string | undefined>();
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [autoLogoutEnabled, setAutoLogoutEnabled] = useState(false);
  const [autoLogoutHour, setAutoLogoutHour] = useState(20);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(true);
  const [autoCloseHour, setAutoCloseHour] = useState(0);
  const [autoCloseActiveQueueEnabled, setAutoCloseActiveQueueEnabled] = useState(true);
  const [autoCloseMenuEnabled, setAutoCloseMenuEnabled] = useState(true);
  const [autoCloseMessageEnabled, setAutoCloseMessageEnabled] = useState(true);
  const [autoCloseMessageHour, setAutoCloseMessageHour] = useState(18);
  const [autoCloseMessageMinute, setAutoCloseMessageMinute] = useState(0);
  const [businessHoursStartHour, setBusinessHoursStartHour] = useState(8);
  const [businessHoursStartMinute, setBusinessHoursStartMinute] = useState(30);
  const [weekendMessageEnabled, setWeekendMessageEnabled] = useState(true);
  const [workingDays, setWorkingDays] = useState<number[]>([1, 2, 3, 4, 5]); // Monday-Friday
  const [satisfactionSurveyEnabled, setSatisfactionSurveyEnabled] = useState(false);
  const [satisfactionSurveyFirstTemplate, setSatisfactionSurveyFirstTemplate] = useState('satisfacao_atendimento');
  const [satisfactionSurveySecondTemplate, setSatisfactionSurveySecondTemplate] = useState('avaliacao_atendimento');
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [firstTemplatePreview, setFirstTemplatePreview] = useState<any>(null);
  const [secondTemplatePreview, setSecondTemplatePreview] = useState<any>(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsModalType, setSettingsModalType] = useState<'success' | 'error'>('success');
  const [settingsModalMessage, setSettingsModalMessage] = useState('');
  const [formData, setFormData] = useState<UpdateProfileForm>({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Password validation helpers
  const isChangingPassword = formData.newPassword || formData.confirmPassword || formData.currentPassword;
  const passwordsMatch = formData.newPassword === formData.confirmPassword;
  const passwordLengthValid = formData.newPassword.length >= 6 || formData.newPassword.length === 0;
  const hasCurrentPassword = formData.currentPassword.length > 0;

  const getPasswordValidationMessage = () => {
    if (!isChangingPassword) return null;

    if (formData.newPassword && !passwordLengthValid) {
      return { type: 'error', message: 'A nova senha deve ter pelo menos 6 caracteres' };
    }

    if (formData.confirmPassword && !passwordsMatch) {
      return { type: 'error', message: 'As senhas não coincidem' };
    }

    if ((formData.newPassword || formData.confirmPassword) && !hasCurrentPassword) {
      return { type: 'error', message: 'Senha atual é obrigatória para alterar a senha' };
    }

    if (formData.newPassword && formData.confirmPassword && passwordsMatch && passwordLengthValid && hasCurrentPassword) {
      return { type: 'success', message: 'Senhas coincidem ✓' };
    }

    return null;
  };

  const isFormValid = () => {
    if (!formData.name || !formData.email) return false;

    if (isChangingPassword) {
      return passwordsMatch && passwordLengthValid && hasCurrentPassword && formData.newPassword.length > 0;
    }

    return true;
  };

  useEffect(() => {
    if (agent) {
      setFormData(prev => ({
        ...prev,
        name: agent.name,
        email: agent.email,
      }));
    }
    loadSettings();
    loadTemplates();
  }, [agent]);

  const loadTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const response = await api.getTemplates();
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
      setTemplates([]);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Update preview when first template is selected
  useEffect(() => {
    const loadFirstTemplateDetails = async () => {
      if (satisfactionSurveyFirstTemplate && templates.length > 0) {
        try {
          const details = await api.getTemplateDetails(satisfactionSurveyFirstTemplate);
          setFirstTemplatePreview(details);
        } catch (error) {
          console.log('Could not load first template details:', error);
          setFirstTemplatePreview(null);
        }
      } else {
        setFirstTemplatePreview(null);
      }
    };
    loadFirstTemplateDetails();
  }, [satisfactionSurveyFirstTemplate, templates]);

  // Update preview when second template is selected
  // Auto-disable autoCloseEnabled if both status filters are disabled
  // Only runs when user actively changes the switches (not on initial load)
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    if (hasInitialized && !autoCloseActiveQueueEnabled && !autoCloseMenuEnabled && autoCloseEnabled) {
      setAutoCloseEnabled(false);
    }
  }, [autoCloseActiveQueueEnabled, autoCloseMenuEnabled, autoCloseEnabled, hasInitialized]);

  // Mark as initialized after first settings load
  useEffect(() => {
    if (!hasInitialized && (autoCloseActiveQueueEnabled !== undefined || autoCloseMenuEnabled !== undefined)) {
      setHasInitialized(true);
    }
  }, [autoCloseActiveQueueEnabled, autoCloseMenuEnabled, hasInitialized]);

  useEffect(() => {
    const loadSecondTemplateDetails = async () => {
      if (satisfactionSurveySecondTemplate && templates.length > 0) {
        try {
          const details = await api.getTemplateDetails(satisfactionSurveySecondTemplate);
          setSecondTemplatePreview(details);
        } catch (error) {
          console.log('Could not load second template details:', error);
          setSecondTemplatePreview(null);
        }
      } else {
        setSecondTemplatePreview(null);
      }
    };
    loadSecondTemplateDetails();
  }, [satisfactionSurveySecondTemplate, templates]);

  const loadSettings = async () => {
    try {
      const settings = await api.getSettings();
      setSystemName(settings.systemName || 'Itororo SAC');
      setSystemLogo(settings.systemLogo);
      setAutoLogoutEnabled(settings.autoLogoutEnabled === 'true' || settings.autoLogoutEnabled === true);
      setAutoLogoutHour(parseInt(settings.autoLogoutHour || '20', 10));
      setAutoCloseEnabled(settings.autoCloseEnabled === 'true' || settings.autoCloseEnabled === true || settings.autoCloseEnabled === undefined);
      setAutoCloseHour(parseInt(settings.autoCloseHour || '0', 10));
      setAutoCloseActiveQueueEnabled(settings.autoCloseActiveQueueEnabled === 'true' || settings.autoCloseActiveQueueEnabled === true || settings.autoCloseActiveQueueEnabled === undefined);
      setAutoCloseMenuEnabled(settings.autoCloseMenuEnabled === 'true' || settings.autoCloseMenuEnabled === true || settings.autoCloseMenuEnabled === undefined);
      setAutoCloseMessageEnabled(settings.autoCloseMessageEnabled === 'true' || settings.autoCloseMessageEnabled === true || settings.autoCloseMessageEnabled === undefined);
      setAutoCloseMessageHour(parseInt(settings.autoCloseMessageHour || '18', 10));
      setAutoCloseMessageMinute(parseInt(settings.autoCloseMessageMinute || '0', 10));
      setBusinessHoursStartHour(parseInt(settings.businessHoursStartHour || '8', 10));
      setBusinessHoursStartMinute(parseInt(settings.businessHoursStartMinute || '30', 10));
      setWeekendMessageEnabled(settings.weekendMessageEnabled === 'true' || settings.weekendMessageEnabled === true || settings.weekendMessageEnabled === undefined);

      // Parse working days (stored as comma-separated string)
      if (settings.workingDays) {
        const days = settings.workingDays.split(',').map(Number).filter(n => !isNaN(n) && n >= 0 && n <= 6);
        setWorkingDays(days.length > 0 ? days : [1, 2, 3, 4, 5]);
      } else {
        setWorkingDays([1, 2, 3, 4, 5]); // Default: Monday-Friday
      }

      // Satisfaction survey settings
      setSatisfactionSurveyEnabled(settings.satisfactionSurveyEnabled === 'true' || settings.satisfactionSurveyEnabled === true);
      setSatisfactionSurveyFirstTemplate(settings.satisfactionSurveyFirstTemplate || 'satisfacao_atendimento');
      setSatisfactionSurveySecondTemplate(settings.satisfactionSurveySecondTemplate || 'avaliacao_atendimento');
    } catch (error) {
      console.error('Failed to load settings:', error);
    }
  };

  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setLogoErrorMessage('Tipo de arquivo inválido. Use apenas imagens (jpg, png, gif, webp, svg)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoErrorMessage('Arquivo muito grande. Tamanho máximo: 5MB');
      return;
    }

    setSelectedLogoFile(file);
    setLogoErrorMessage('');

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!selectedLogoFile) return;

    setIsUploadingLogo(true);
    setLogoSuccessMessage('');
    setLogoErrorMessage('');

    try {
      const result = await api.uploadSystemLogo(selectedLogoFile);
      setSystemLogo(result.logoUrl);
      setLogoSuccessMessage('Logo enviado com sucesso! Atualize a página para ver as mudanças.');
      setSelectedLogoFile(null);
      setLogoPreview(null);
    } catch (error: any) {
      console.error('Failed to upload logo:', error);
      setLogoErrorMessage(error.response?.data?.error || 'Falha ao enviar logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    if (!window.confirm('Tem certeza que deseja remover o logo do sistema?')) return;

    setIsUploadingLogo(true);
    setLogoSuccessMessage('');
    setLogoErrorMessage('');

    try {
      await api.deleteSystemLogo();
      setSystemLogo(undefined);
      setLogoSuccessMessage('Logo removido com sucesso! Atualize a página para ver as mudanças.');
    } catch (error: any) {
      console.error('Failed to delete logo:', error);
      setLogoErrorMessage(error.response?.data?.error || 'Falha ao remover logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    // Validate passwords match if changing password
    if (formData.newPassword || formData.confirmPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setErrorMessage('As senhas não coincidem');
        return;
      }
      if (formData.newPassword.length < 6) {
        setErrorMessage('A nova senha deve ter pelo menos 6 caracteres');
        return;
      }
      if (!formData.currentPassword) {
        setErrorMessage('Senha atual é obrigatória para alterar a senha');
        return;
      }
    }

    setIsLoading(true);

    try {
      const updateData: any = {
        name: formData.name,
      };

      // Only include email if not ADMIN (ADMIN users cannot change their email)
      if (agent?.role !== 'ADMIN') {
        updateData.email = formData.email;
      }

      if (formData.currentPassword && formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      await api.updateAdminProfile(updateData);

      setSuccessMessage('Perfil atualizado com sucesso!');
      setFormData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      }));

      // Reload page after 1.5 seconds to refresh the auth context
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      setErrorMessage(error.response?.data?.error || 'Falha ao atualizar perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSettingsSuccessMessage('');
    setSettingsErrorMessage('');

    if (!systemName.trim()) {
      setSettingsModalType('error');
      setSettingsModalMessage('O nome do sistema não pode estar vazio');
      setShowSettingsModal(true);
      return;
    }

    if (autoLogoutHour < 0 || autoLogoutHour > 23) {
      setSettingsModalType('error');
      setSettingsModalMessage('O horário de logout deve estar entre 0 e 23');
      setShowSettingsModal(true);
      return;
    }

    if (autoCloseHour < 0 || autoCloseHour > 23) {
      setSettingsModalType('error');
      setSettingsModalMessage('O horário de encerramento automático deve estar entre 0 e 23');
      setShowSettingsModal(true);
      return;
    }

    if (autoCloseMessageHour < 0 || autoCloseMessageHour > 23) {
      setSettingsModalType('error');
      setSettingsModalMessage('O horário de fechamento de expediente deve estar entre 0 e 23');
      setShowSettingsModal(true);
      return;
    }

    if (autoCloseMessageMinute < 0 || autoCloseMessageMinute > 59) {
      setSettingsModalType('error');
      setSettingsModalMessage('Os minutos devem estar entre 0 e 59');
      setShowSettingsModal(true);
      return;
    }

    if (weekendMessageEnabled && workingDays.length === 0) {
      setSettingsModalType('error');
      setSettingsModalMessage('Selecione pelo menos um dia de funcionamento');
      setShowSettingsModal(true);
      return;
    }

    // Validation: if both autoClose filters are disabled, disable autoClose
    if (autoCloseEnabled && !autoCloseActiveQueueEnabled && !autoCloseMenuEnabled) {
      setSettingsModalType('error');
      setSettingsModalMessage('Pelo menos uma das opções de fechamento automático deve estar habilitada (Ativos/Fila ou Menu)');
      setShowSettingsModal(true);
      return;
    }

    setIsLoadingSettings(true);

    try {
      await api.updateSettings({
        systemName: systemName.trim(),
        autoLogoutEnabled: autoLogoutEnabled.toString(),
        autoLogoutHour: autoLogoutHour.toString(),
        autoLogoutTimezone: 'America/Sao_Paulo',
        autoCloseEnabled: autoCloseEnabled.toString(),
        autoCloseHour: autoCloseHour.toString(),
        autoCloseTimezone: 'America/Sao_Paulo',
        autoCloseActiveQueueEnabled: autoCloseActiveQueueEnabled.toString(),
        autoCloseMenuEnabled: autoCloseMenuEnabled.toString(),
        autoCloseMessageEnabled: autoCloseMessageEnabled.toString(),
        autoCloseMessageHour: autoCloseMessageHour.toString(),
        autoCloseMessageMinute: autoCloseMessageMinute.toString(),
        businessHoursStartHour: businessHoursStartHour.toString(),
        businessHoursStartMinute: businessHoursStartMinute.toString(),
        weekendMessageEnabled: weekendMessageEnabled.toString(),
        workingDays: workingDays.sort((a, b) => a - b).join(','), // Store as comma-separated string
        satisfactionSurveyEnabled: satisfactionSurveyEnabled.toString(),
        satisfactionSurveyFirstTemplate: satisfactionSurveyFirstTemplate,
        satisfactionSurveySecondTemplate: satisfactionSurveySecondTemplate,
      });
      setSettingsModalType('success');
      setSettingsModalMessage('Configurações atualizadas com sucesso! Atualize a página para ver as mudanças.');
      setShowSettingsModal(true);
    } catch (error: any) {
      console.error('Failed to update settings:', error);
      setSettingsModalType('error');
      setSettingsModalMessage(error.response?.data?.error || 'Falha ao atualizar configurações');
      setShowSettingsModal(true);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-3">
        <div className="bg-purple-100 dark:bg-purple-900/30 p-3 rounded-lg">
          <Shield className="w-8 h-8 text-purple-600 dark:text-purple-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Administração do Sistema</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Gerenciar configurações do sistema e perfil de administrador</p>
        </div>
      </div>

      {/* System Settings and Logo - Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* System Settings Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
          <Settings className="w-6 h-6" />
          <span>Configurações do Sistema</span>
        </h2>

        {settingsSuccessMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {settingsSuccessMessage}
          </div>
        )}

        {settingsErrorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {settingsErrorMessage}
          </div>
        )}

        <form onSubmit={handleSettingsSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Nome do Sistema/Aplicação *
            </label>
            <input
              type="text"
              value={systemName}
              onChange={(e) => setSystemName(e.target.value)}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Ex: Itororo SAC"
            />
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
              Este nome aparecerá na tela de login e no cabeçalho do sistema
            </p>
          </div>

          {/* Auto Logout Configuration */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Logout Automático
            </h3>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ativar Logout Automático
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Os agentes serão automaticamente deslogados no horário configurado
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAutoLogoutEnabled(!autoLogoutEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  autoLogoutEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoLogoutEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Logout Hour Configuration */}
            {autoLogoutEnabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Horário de Logout (0-23) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={autoLogoutHour}
                    onChange={(e) => setAutoLogoutHour(parseInt(e.target.value, 10) || 0)}
                    required={autoLogoutEnabled}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    placeholder="20"
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Os agentes serão deslogados automaticamente às {autoLogoutHour}:00
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>Timezone:</strong> America/Sao_Paulo (Horário de Brasília)<br />
                    <strong>Como funciona:</strong> Todos os agentes logados serão automaticamente deslogados no horário configurado.
                    Se deixarem o navegador aberto, verão a tela de login no dia seguinte.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Auto Close Conversations Configuration */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Encerramento Automático de Atendimentos
            </h3>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ativar Encerramento Automático
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Todos os atendimentos ativos serão encerrados automaticamente no horário configurado
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAutoCloseEnabled(!autoCloseEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  autoCloseEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoCloseEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Auto Close Hour Configuration */}
            {autoCloseEnabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Horário de Encerramento (0-23) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={autoCloseHour}
                    onChange={(e) => setAutoCloseHour(parseInt(e.target.value, 10) || 0)}
                    required={autoCloseEnabled}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                    placeholder="0"
                  />
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Todos os atendimentos ativos serão encerrados automaticamente às {autoCloseHour}:00
                  </p>
                </div>

                {/* Status Filters */}
                <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-4">
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Quais atendimentos devem ser fechados automaticamente?
                  </h4>

                  {/* Active/Queue Switch */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Atendimentos Ativos e em Fila
                      </label>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Fecha atendimentos com status ACTIVE, QUEUED e WAITING
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAutoCloseActiveQueueEnabled(!autoCloseActiveQueueEnabled)}
                      disabled={!autoCloseEnabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        autoCloseActiveQueueEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoCloseActiveQueueEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Menu Switch */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Atendimentos em Menu
                      </label>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Fecha atendimentos com status MENU
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setAutoCloseMenuEnabled(!autoCloseMenuEnabled)}
                      disabled={!autoCloseEnabled}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        autoCloseMenuEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          autoCloseMenuEnabled ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>Timezone:</strong> America/Sao_Paulo (Horário de Brasília)<br />
                    <strong>Como funciona:</strong> No horário configurado, os atendimentos dos tipos selecionados acima serão automaticamente encerrados, enviando uma mensagem de encerramento ao cliente via WhatsApp.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Auto Close Message Configuration */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Mensagem de Fechamento de Expediente
            </h3>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ativar Mensagem de Fechamento
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enviar mensagem automática quando cliente contatar fora do horário de expediente
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAutoCloseMessageEnabled(!autoCloseMessageEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  autoCloseMessageEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    autoCloseMessageEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Auto Close Message Hour Configuration */}
            {autoCloseMessageEnabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Horário de Início do Expediente *
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Hora (0-23)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={businessHoursStartHour}
                        onChange={(e) => setBusinessHoursStartHour(parseInt(e.target.value, 10) || 0)}
                        required={autoCloseMessageEnabled}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        placeholder="8"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Minutos (0-59)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={businessHoursStartMinute}
                        onChange={(e) => setBusinessHoursStartMinute(parseInt(e.target.value, 10) || 0)}
                        required={autoCloseMessageEnabled}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        placeholder="30"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Clientes que entrarem em contato antes das {businessHoursStartHour.toString().padStart(2, '0')}:{businessHoursStartMinute.toString().padStart(2, '0')} receberão mensagem automática informando que o atendimento ainda não começou
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Horário de Fechamento do Expediente *
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Hora (0-23)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="23"
                        value={autoCloseMessageHour}
                        onChange={(e) => setAutoCloseMessageHour(parseInt(e.target.value, 10) || 0)}
                        required={autoCloseMessageEnabled}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        placeholder="18"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        Minutos (0-59)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="59"
                        value={autoCloseMessageMinute}
                        onChange={(e) => setAutoCloseMessageMinute(parseInt(e.target.value, 10) || 0)}
                        required={autoCloseMessageEnabled}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Clientes que entrarem em contato após às {autoCloseMessageHour.toString().padStart(2, '0')}:{autoCloseMessageMinute.toString().padStart(2, '0')} receberão mensagem automática informando que o atendimento está encerrado
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>Timezone:</strong> America/Sao_Paulo (Horário de Brasília)<br />
                    <strong>Como funciona:</strong> Quando um cliente iniciar uma conversa antes de {businessHoursStartHour.toString().padStart(2, '0')}:{businessHoursStartMinute.toString().padStart(2, '0')} ou após {autoCloseMessageHour.toString().padStart(2, '0')}:{autoCloseMessageMinute.toString().padStart(2, '0')}, receberá uma mensagem automática informando que o atendimento está fora do horário, e a conversa será fechada automaticamente.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Weekend Message Configuration */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Mensagem de Dias Não Úteis
            </h3>

            {/* Enable/Disable Toggle */}
            <div className="flex items-center justify-between mb-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Ativar Mensagem de Dias Não Úteis
                </label>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enviar mensagem automática quando cliente contatar em dias que a empresa não funciona
                </p>
              </div>
              <button
                type="button"
                onClick={() => setWeekendMessageEnabled(!weekendMessageEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${
                  weekendMessageEnabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    weekendMessageEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Working Days Configuration */}
            {weekendMessageEnabled && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Dias de Funcionamento *
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      { value: 0, label: 'Domingo' },
                      { value: 1, label: 'Segunda' },
                      { value: 2, label: 'Terça' },
                      { value: 3, label: 'Quarta' },
                      { value: 4, label: 'Quinta' },
                      { value: 5, label: 'Sexta' },
                      { value: 6, label: 'Sábado' },
                    ].map((day) => (
                      <label
                        key={day.value}
                        className={`flex items-center space-x-2 p-3 border rounded-lg cursor-pointer transition ${
                          workingDays.includes(day.value)
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                            : 'border-gray-300 dark:border-gray-600 hover:border-purple-300 dark:hover:border-purple-700'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={workingDays.includes(day.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setWorkingDays([...workingDays, day.value].sort((a, b) => a - b));
                            } else {
                              setWorkingDays(workingDays.filter((d) => d !== day.value));
                            }
                          }}
                          className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{day.label}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                    Selecione os dias em que a empresa funciona. Clientes que entrarem em contato nos dias não selecionados receberão mensagem automática.
                  </p>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <p className="text-sm text-blue-700 dark:text-blue-400">
                    <strong>Timezone:</strong> America/Sao_Paulo (Horário de Brasília)<br />
                    <strong>Como funciona:</strong> Quando um cliente iniciar uma conversa em um dia não selecionado, receberá uma mensagem automática informando os dias e horários de funcionamento, e a conversa será fechada automaticamente.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Satisfaction Survey Settings */}
          <div className="border-t dark:border-gray-700 pt-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                  <svg className="w-6 h-6 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Pesquisa de Satisfação</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Enviar pesquisa ao encerrar atendimentos</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSatisfactionSurveyEnabled(!satisfactionSurveyEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  satisfactionSurveyEnabled ? 'bg-green-600' : 'bg-gray-200 dark:bg-gray-700'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    satisfactionSurveyEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {satisfactionSurveyEnabled && (
              <div className="space-y-4 mt-4 pl-14">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template da Primeira Pergunta
                  </label>
                  {loadingTemplates ? (
                    <div className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Carregando templates...</span>
                    </div>
                  ) : (
                    <select
                      value={satisfactionSurveyFirstTemplate}
                      onChange={(e) => setSatisfactionSurveyFirstTemplate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                    >
                      <option value="">Selecione um template</option>
                      {templates
                        .filter(t => t.status === 'APPROVED')
                        .map(template => (
                          <option key={template.name} value={template.name}>
                            {template.name} - {template.language}
                          </option>
                        ))}
                    </select>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Template para "O atendimento foi satisfatório?" (Sim/Não)
                  </p>

                  {/* Preview do Template */}
                  {firstTemplatePreview && firstTemplatePreview.components && (
                    <div className="mt-3 p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg space-y-3">
                      <p className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase">📱 Preview da Mensagem</p>

                      {/* Header */}
                      {firstTemplatePreview.components.find((c: any) => c.type === 'HEADER') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cabeçalho:</p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium bg-white dark:bg-gray-800 p-2 rounded">
                            {firstTemplatePreview.components.find((c: any) => c.type === 'HEADER')?.text}
                          </p>
                        </div>
                      )}

                      {/* Body */}
                      {firstTemplatePreview.components.find((c: any) => c.type === 'BODY') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Corpo:</p>
                          <p className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded whitespace-pre-wrap">
                            {firstTemplatePreview.components.find((c: any) => c.type === 'BODY')?.text}
                          </p>
                        </div>
                      )}

                      {/* Footer */}
                      {firstTemplatePreview.components.find((c: any) => c.type === 'FOOTER') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rodapé:</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded">
                            {firstTemplatePreview.components.find((c: any) => c.type === 'FOOTER')?.text}
                          </p>
                        </div>
                      )}

                      {/* Buttons */}
                      {firstTemplatePreview.components.find((c: any) => c.type === 'BUTTONS')?.buttons && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Botões:</p>
                          <div className="space-y-2">
                            {firstTemplatePreview.components.find((c: any) => c.type === 'BUTTONS')?.buttons.map((btn: any, idx: number) => (
                              <div key={idx} className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-sm text-center font-medium">
                                {btn.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-2">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      ⚠️ <strong>Importante:</strong> O template deve ter 2 botões configurados:<br />
                      • Botão 1: "✅ Sim" (ID: satisfaction_yes)<br />
                      • Botão 2: "❌ Não" (ID: satisfaction_no)
                    </p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Template da Segunda Pergunta
                  </label>
                  {loadingTemplates ? (
                    <div className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-yellow-600"></div>
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">Carregando templates...</span>
                    </div>
                  ) : (
                    <select
                      value={satisfactionSurveySecondTemplate}
                      onChange={(e) => setSatisfactionSurveySecondTemplate(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent outline-none"
                    >
                      <option value="">Selecione um template</option>
                      {templates
                        .filter(t => t.status === 'APPROVED')
                        .map(template => (
                          <option key={template.name} value={template.name}>
                            {template.name} - {template.language}
                          </option>
                        ))}
                    </select>
                  )}
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Template para "Como você avaliaria?" (Ruim/Médio/Bom)
                  </p>

                  {/* Preview do Template */}
                  {secondTemplatePreview && secondTemplatePreview.components && (
                    <div className="mt-3 p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg space-y-3">
                      <p className="text-xs font-semibold text-purple-700 dark:text-purple-400 uppercase">📱 Preview da Mensagem</p>

                      {/* Header */}
                      {secondTemplatePreview.components.find((c: any) => c.type === 'HEADER') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Cabeçalho:</p>
                          <p className="text-sm text-gray-900 dark:text-white font-medium bg-white dark:bg-gray-800 p-2 rounded">
                            {secondTemplatePreview.components.find((c: any) => c.type === 'HEADER')?.text}
                          </p>
                        </div>
                      )}

                      {/* Body */}
                      {secondTemplatePreview.components.find((c: any) => c.type === 'BODY') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Corpo:</p>
                          <p className="text-sm text-gray-900 dark:text-white bg-white dark:bg-gray-800 p-2 rounded whitespace-pre-wrap">
                            {secondTemplatePreview.components.find((c: any) => c.type === 'BODY')?.text}
                          </p>
                        </div>
                      )}

                      {/* Footer */}
                      {secondTemplatePreview.components.find((c: any) => c.type === 'FOOTER') && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Rodapé:</p>
                          <p className="text-sm text-gray-600 dark:text-gray-400 bg-white dark:bg-gray-800 p-2 rounded">
                            {secondTemplatePreview.components.find((c: any) => c.type === 'FOOTER')?.text}
                          </p>
                        </div>
                      )}

                      {/* Buttons */}
                      {secondTemplatePreview.components.find((c: any) => c.type === 'BUTTONS')?.buttons && (
                        <div>
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Botões:</p>
                          <div className="space-y-2">
                            {secondTemplatePreview.components.find((c: any) => c.type === 'BUTTONS')?.buttons.map((btn: any, idx: number) => (
                              <div key={idx} className="px-3 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded text-sm text-center font-medium">
                                {btn.text}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mt-2">
                    <p className="text-xs text-blue-700 dark:text-blue-400">
                      ⚠️ <strong>Importante:</strong> O template deve ter 3 botões configurados:<br />
                      • Botão 1: "😞 Ruim" (ID: rating_bad)<br />
                      • Botão 2: "😐 Médio" (ID: rating_medium)<br />
                      • Botão 3: "😊 Bom" (ID: rating_good)
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <p className="text-sm text-yellow-700 dark:text-yellow-400">
                    <strong>Quando envia:</strong> A pesquisa é enviada automaticamente quando um atendimento ATIVO é encerrado manualmente pelo atendente.<br />
                    <strong>Não envia se:</strong> Atendimento foi iniciado pelo atendente, foi fechado automaticamente, ou não estava ATIVO.<br />
                    <strong>Templates:</strong> Os templates devem ser criados previamente na API do WhatsApp com os botões especificados acima.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button - Positioned at bottom to show it saves ALL settings */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={isLoadingSettings}
              className="w-full px-6 py-4 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed text-base font-semibold"
            >
              {isLoadingSettings ? (
                <>
                  <svg className="animate-spin h-6 w-6 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Salvando Configurações...</span>
                </>
              ) : (
                <>
                  <Save className="w-6 h-6" />
                  <span>Salvar Todas as Configurações do Sistema</span>
                </>
              )}
            </button>
            <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2">
              Este botão salva todas as configurações do sistema acima (nome, logout, encerramento, horários, dias úteis e pesquisa de satisfação)
            </p>
          </div>
        </form>
      </div>

        {/* System Logo Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center space-x-2">
          <Image className="w-6 h-6" />
          <span>Logo do Sistema</span>
        </h2>

        {logoSuccessMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {logoSuccessMessage}
          </div>
        )}

        {logoErrorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {logoErrorMessage}
          </div>
        )}

        <div className="space-y-6">
          {/* Current Logo Display */}
          {systemLogo && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Logo Atual
              </label>
              <div className="flex items-center space-x-4">
                <div className="flex-shrink-0 w-32 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                  <img
                    src={`${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${systemLogo}`}
                    alt="System Logo"
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                    Este logo é usado como favicon (ícone da aba do navegador), na tela de login e na barra de navegação
                  </p>
                  <button
                    onClick={handleLogoDelete}
                    disabled={isUploadingLogo}
                    className="px-4 py-2 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Remover Logo</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upload New Logo */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {systemLogo ? 'Alterar Logo' : 'Adicionar Logo'}
            </label>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <input
                  type="file"
                  id="logo-upload"
                  accept="image/*"
                  onChange={handleLogoFileChange}
                  disabled={isUploadingLogo}
                  className="hidden"
                />
                <label
                  htmlFor="logo-upload"
                  className={`flex items-center justify-center space-x-2 px-4 py-3 border-2 border-dashed rounded-lg cursor-pointer transition ${
                    isUploadingLogo
                      ? 'opacity-50 cursor-not-allowed'
                      : 'border-gray-300 dark:border-gray-600 hover:border-purple-500 dark:hover:border-purple-400'
                  }`}
                >
                  <Upload className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Clique para selecionar uma imagem
                  </span>
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Formatos aceitos: JPG, PNG, GIF, WEBP, SVG (max 5MB)
                </p>
              </div>

              {/* Preview */}
              {logoPreview && (
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Pré-visualização
                  </label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0 w-32 h-32 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center overflow-hidden border-2 border-gray-300 dark:border-gray-600">
                      <img
                        src={logoPreview}
                        alt="Preview"
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 space-y-2">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {selectedLogoFile?.name}
                      </p>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={handleLogoUpload}
                          disabled={isUploadingLogo}
                          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                        >
                          {isUploadingLogo && (
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                          )}
                          <Upload className="w-4 h-4" />
                          <span>{isUploadingLogo ? 'Enviando...' : 'Enviar Logo'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setSelectedLogoFile(null);
                            setLogoPreview(null);
                          }}
                          disabled={isUploadingLogo}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Profile Settings Card - Full Width */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">Informações do Perfil</h2>

        {successMessage && (
          <div className="mb-4 p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Current Role Display */}
          <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-300">
                Função: Administrador do Sistema
              </span>
            </div>
          </div>

          {/* Name and Email - Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>Nome *</span>
              </div>
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
              placeholder="Seu nome completo"
            />
          </div>

          {/* Email Field - Disabled for ADMIN users */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <div className="flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Email *</span>
                {agent?.role === 'ADMIN' && (
                  <span className="text-xs text-purple-600 dark:text-purple-400">(não editável)</span>
                )}
              </div>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              disabled={agent?.role === 'ADMIN'}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-600"
              placeholder="seu@email.com"
            />
            {agent?.role === 'ADMIN' && (
              <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
                Por motivos de segurança, o email de administradores do sistema não pode ser alterado
              </p>
            )}
          </div>
          </div>

          {/* Password Change Section - Hidden for ADMIN users */}
          {agent?.role !== 'ADMIN' ? (
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                <Lock className="w-5 h-5" />
                <span>Alterar Senha</span>
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Deixe os campos de senha em branco se não deseja alterá-la
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Senha Atual {isChangingPassword && <span className="text-red-500">*</span>}
                  </label>
                  <input
                    type="password"
                    value={formData.currentPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, currentPassword: e.target.value });
                      setErrorMessage('');
                    }}
                    className={`w-full px-4 py-2 border ${
                      isChangingPassword && !hasCurrentPassword
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    placeholder="Sua senha atual"
                  />
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Nova Senha
                  </label>
                  <input
                    type="password"
                    value={formData.newPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, newPassword: e.target.value });
                      setErrorMessage('');
                    }}
                    className={`w-full px-4 py-2 border ${
                      formData.newPassword && !passwordLengthValid
                        ? 'border-red-300 dark:border-red-600'
                        : 'border-gray-300 dark:border-gray-600'
                    } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                  />
                  {formData.newPassword && !passwordLengthValid && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      ⚠️ Mínimo 6 caracteres
                    </p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Confirmar Nova Senha
                  </label>
                  <input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      setFormData({ ...formData, confirmPassword: e.target.value });
                      setErrorMessage('');
                    }}
                    className={`w-full px-4 py-2 border ${
                      formData.confirmPassword && !passwordsMatch
                        ? 'border-red-300 dark:border-red-600'
                        : formData.confirmPassword && passwordsMatch
                        ? 'border-green-300 dark:border-green-600'
                        : 'border-gray-300 dark:border-gray-600'
                    } dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none`}
                    placeholder="Digite a nova senha novamente"
                    minLength={6}
                  />
                  {formData.confirmPassword && !passwordsMatch && (
                    <p className="text-xs text-red-600 dark:text-red-400 mt-1">
                      ⚠️ As senhas não coincidem
                    </p>
                  )}
                  {formData.confirmPassword && passwordsMatch && formData.newPassword && (
                    <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                      ✓ As senhas coincidem
                    </p>
                  )}
                </div>

                {/* Overall validation message */}
                {(() => {
                  const validationMsg = getPasswordValidationMessage();
                  if (!validationMsg) return null;

                  return (
                    <div className={`p-3 rounded-lg ${
                      validationMsg.type === 'error'
                        ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
                        : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                    }`}>
                      <p className="text-sm">{validationMsg.message}</p>
                    </div>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Lock className="w-5 h-5 text-purple-600 dark:text-purple-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-purple-900 dark:text-purple-300 mb-1">
                      Alteração de Senha Desabilitada
                    </h3>
                    <p className="text-sm text-purple-700 dark:text-purple-400">
                      Por motivos de segurança, administradores do sistema não podem alterar sua própria senha através desta interface.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center space-x-3 pt-4">
            <button
              type="submit"
              disabled={isLoading || !isFormValid()}
              className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 dark:bg-purple-500 dark:hover:bg-purple-600 text-white rounded-lg transition flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Salvando...</span>
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  <span>Salvar Alterações</span>
                </>
              )}
            </button>
          </div>
          {!isFormValid() && isChangingPassword && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 text-center">
              Complete todos os campos de senha corretamente para salvar as alterações
            </p>
          )}
        </form>
      </div>

      {/* Settings Feedback Modal */}
      {showSettingsModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={() => setShowSettingsModal(false)} />

            <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl transform transition-all w-full max-w-md z-50">
              <div className="p-6">
                {/* Icon */}
                <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${
                  settingsModalType === 'success'
                    ? 'bg-green-100 dark:bg-green-900/30'
                    : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  {settingsModalType === 'success' ? (
                    <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>

                {/* Title */}
                <h3 className={`text-lg font-semibold text-center mb-2 ${
                  settingsModalType === 'success'
                    ? 'text-green-900 dark:text-green-300'
                    : 'text-red-900 dark:text-red-300'
                }`}>
                  {settingsModalType === 'success' ? 'Sucesso!' : 'Erro'}
                </h3>

                {/* Message */}
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                  {settingsModalMessage}
                </p>

                {/* Close Button */}
                <button
                  onClick={() => setShowSettingsModal(false)}
                  className={`w-full px-4 py-2 rounded-lg font-medium transition ${
                    settingsModalType === 'success'
                      ? 'bg-green-600 hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 text-white'
                      : 'bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white'
                  }`}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
